import { useState } from 'react';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2FormGrid,
  V2FormField,
  V2FormActions,
  Icon,
} from '@sakhtar/design-system/react';
import { fromJalali, jalaliMonthLength, systemClock, toJalali } from '@sakhtar/calendar';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { SearchableSelect } from './ModuleAnswersStep.js';
import type {
  BaseOrganizationPatch,
  BaseOrganizationPayload,
  SetupActivityModel,
  SetupAmountDisplayUnit,
  SetupIndustry,
  SetupPersonType,
  SetupTeamSizeRange,
  SetupVatMode,
} from '../../lib/types/setup.js';

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function faDigits(value: string | number): string {
  return String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!);
}

const PERSON_TYPE_OPTIONS: readonly { value: SetupPersonType; label: string }[] = [
  { value: 'individual', label: 'شخص حقیقی' },
  { value: 'company', label: 'شرکت ثبت‌شده' },
  { value: 'undecided', label: 'هنوز مشخص نیست' },
];

const INDUSTRY_OPTIONS: readonly { value: SetupIndustry; label: string }[] = [
  { value: 'trade', label: 'بازرگانی' },
  { value: 'retail', label: 'خرده‌فروشی' },
  { value: 'services', label: 'خدمات' },
  { value: 'manufacturing', label: 'تولید' },
  { value: 'contracting', label: 'پیمانکاری' },
  { value: 'distribution', label: 'پخش' },
  { value: 'other', label: 'سایر' },
];

const ACTIVITY_MODEL_OPTIONS: readonly { value: SetupActivityModel; label: string }[] = [
  { value: 'goods', label: 'فروش کالا' },
  { value: 'services', label: 'ارائهٔ خدمات' },
  { value: 'projects', label: 'اجرای پروژه' },
];

const TEAM_SIZE_OPTIONS: readonly { value: SetupTeamSizeRange; label: string }[] = [
  { value: 'solo', label: 'فقط خودم' },
  { value: '2-5', label: '۲ تا ۵ نفر' },
  { value: '6-15', label: '۶ تا ۱۵ نفر' },
  { value: '16-30', label: '۱۶ تا ۳۰ نفر' },
  { value: 'more', label: 'بیشتر' },
];

const VAT_OPTIONS: readonly { value: SetupVatMode; label: string; description: string }[] = [
  {
    value: 'enabled',
    label: 'مشمول هستیم',
    description: 'ارزش افزوده در فاکتورهای شما محاسبه می‌شود.',
  },
  { value: 'disabled', label: 'مشمول نیستیم', description: '' },
  {
    value: 'skipped',
    label: 'بعداً با حسابدار بررسی می‌کنم',
    description: 'می‌توانید هر زمان این تصمیم را در تنظیمات تغییر دهید.',
  },
];

const AMOUNT_DISPLAY_OPTIONS: readonly {
  value: SetupAmountDisplayUnit;
  label: string;
  example: string;
}[] = [
  { value: 'toman', label: 'تومان', example: 'مثال: ۱٬۰۰۰٬۰۰۰ تومان' },
  { value: 'rial', label: 'ریال', example: 'مثال: ۱۰٬۰۰۰٬۰۰۰ ریال' },
];

const PADDING_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

function samplePadded(padding: number): string {
  return '1'.padStart(padding, '0');
}

/** A suggested, already-complete fiscal year for the current Jalali year
 * (Farvardin 1 through the last day of Esfand) — accepted as-is unless the
 * owner opens "ویرایش". */
function suggestedFiscalYear(): { code: string; name: string; startDate: string; endDate: string } {
  const jy = toJalali(systemClock.today()).jy;
  return {
    code: String(jy),
    name: `سال مالی ${faDigits(jy)}`,
    startDate: fromJalali({ jy, jm: 1, jd: 1 }),
    endDate: fromJalali({ jy, jm: 12, jd: jalaliMonthLength(jy, 12) }),
  };
}

/** Radio-style option row with an optional description — same visual
 * language as ModuleAnswersStep's 'radio' field kind, kept local here since
 * this step isn't driven by that generic config/renderer. */
function RadioRow<T extends string>({
  value,
  active,
  label,
  description,
  onClick,
}: {
  value: T;
  active: boolean;
  label: string;
  description?: string | undefined;
  onClick: () => void;
}) {
  return (
    <button
      key={value}
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'start',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '11px 13px',
        borderRadius: 'var(--sk-r-lg)',
        border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
        background: active
          ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
          : 'var(--sk-bg-canvas)',
      }}
    >
      <span
        style={{
          marginTop: 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border-strong)'}`,
          display: 'grid',
          placeContent: 'center',
          flex: '0 0 auto',
        }}
      >
        {active ? (
          <span
            style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sk-brand-500)' }}
          />
        ) : null}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>{label}</span>
        {description ? (
          <span style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}>{description}</span>
        ) : null}
      </span>
    </button>
  );
}

function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '8px 14px',
    borderRadius: 'var(--sk-r-full, 999px)',
    border: `1px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
    background: active
      ? 'color-mix(in oklch, var(--sk-brand-500), transparent 88%)'
      : 'transparent',
    color: active ? 'var(--sk-brand-500)' : 'var(--sk-fg)',
    fontSize: 13,
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
  };
}

/**
 * Step 2 — "شناخت کسب‌وکار" (folded in, no new step key — see
 * `apps/api/src/setup/contract.ts`'s own doc comment on why), "تنظیمات
 * پایه" and "اطلاعاتی که روی اسناد شما نمایش داده می‌شود", per the repo
 * owner's 2026-09-27 field-by-field question/control-type review. Fixed
 * invariants (calendar/timezone/currency) render read-only, never editable,
 * never sent back to the server.
 *
 * Only personType/industry/amountDisplayUnit gate completion now
 * (`assertBaseOrganizationComplete`'s new, narrower rule) — fiscal
 * year/VAT/document numbering/primary location are all answerable with an
 * already-valid suggested default (fiscal year is pre-seeded and complete
 * from first render; VAT/numbering/location start unset and stay optional).
 * The legal-identity section (section 3) shows the registered-company pair
 * or the natural-person pair depending on `personType`, and neither pair
 * when it's still 'undecided'.
 */
export function BaseOrganizationStep({
  invariants,
  initialPayload,
  onSave,
  onComplete,
  onBack,
  busy,
  error,
}: {
  invariants: { readonly calendar: string; readonly timezone: string; readonly currency: string };
  initialPayload: BaseOrganizationPayload;
  onSave: (patch: BaseOrganizationPatch) => Promise<void>;
  onComplete: (patch: BaseOrganizationPatch) => Promise<void>;
  onBack: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [personType, setPersonType] = useState<SetupPersonType | null>(
    initialPayload.personType ?? null,
  );
  const [industry, setIndustry] = useState<SetupIndustry | null>(initialPayload.industry ?? null);
  const [industryOtherNote, setIndustryOtherNote] = useState(
    initialPayload.industryOtherNote ?? '',
  );
  const [activityModel, setActivityModel] = useState<readonly SetupActivityModel[]>(
    initialPayload.activityModel ?? [],
  );
  const [teamSizeRange, setTeamSizeRange] = useState<SetupTeamSizeRange | ''>(
    initialPayload.teamSizeRange ?? '',
  );

  const [fyEditing, setFyEditing] = useState(false);
  const fySuggested = useState(suggestedFiscalYear)[0];
  const [fyCode, setFyCode] = useState(initialPayload.fiscalYear?.code ?? fySuggested.code);
  const [fyName, setFyName] = useState(initialPayload.fiscalYear?.name ?? fySuggested.name);
  const [fyStart, setFyStart] = useState(
    initialPayload.fiscalYear?.startDate ?? fySuggested.startDate,
  );
  const [fyEnd, setFyEnd] = useState(initialPayload.fiscalYear?.endDate ?? fySuggested.endDate);

  const [vat, setVat] = useState<SetupVatMode | ''>(initialPayload.vat ?? '');
  const [amountDisplayUnit, setAmountDisplayUnit] = useState<SetupAmountDisplayUnit | ''>(
    initialPayload.amountDisplayUnit ?? '',
  );

  const [docEditing, setDocEditing] = useState(false);
  const [docPrefix, setDocPrefix] = useState(initialPayload.documentNumbering?.prefix ?? 'INV');
  const [docSeries, setDocSeries] = useState(initialPayload.documentNumbering?.series ?? 'A');
  const [docPadding, setDocPadding] = useState(initialPayload.documentNumbering?.padding ?? 4);

  const [locName, setLocName] = useState(initialPayload.primaryLocation?.name ?? '');

  const [invoiceDisplayName, setInvoiceDisplayName] = useState(
    initialPayload.legalIdentity?.invoiceDisplayName ?? '',
  );
  const [legalName, setLegalName] = useState(initialPayload.legalIdentity?.legalName ?? '');
  const [nationalId, setNationalId] = useState(initialPayload.legalIdentity?.nationalId ?? '');
  const [ownerFirstName, setOwnerFirstName] = useState(
    initialPayload.legalIdentity?.ownerFirstName ?? '',
  );
  const [ownerLastName, setOwnerLastName] = useState(
    initialPayload.legalIdentity?.ownerLastName ?? '',
  );
  const [economicCode, setEconomicCode] = useState(
    initialPayload.legalIdentity?.economicCode ?? '',
  );
  const [legalPhone, setLegalPhone] = useState(initialPayload.legalIdentity?.phone ?? '');
  const [legalAddress, setLegalAddress] = useState(initialPayload.legalIdentity?.address ?? '');

  const [localError, setLocalError] = useState<string | null>(null);

  function draft(): BaseOrganizationPatch {
    const fiscalYear =
      fyCode.trim() && fyName.trim() && fyStart && fyEnd
        ? { code: fyCode.trim(), name: fyName.trim(), startDate: fyStart, endDate: fyEnd }
        : null;
    const documentNumbering =
      docPrefix.trim() && docSeries.trim()
        ? { prefix: docPrefix.trim(), series: docSeries.trim(), padding: docPadding }
        : null;
    // The backend contract still requires a `code`, distinct from `name` —
    // the repo owner's review wants only one plain text field here ("کد
    // داخلی خودکار ساخته شود"), and there is no real code-generation
    // endpoint to call from here, so `code` reuses the same trimmed name.
    const primaryLocation = locName.trim() ? { code: locName.trim(), name: locName.trim() } : null;
    const hasIdentity =
      invoiceDisplayName.trim() ||
      legalName.trim() ||
      nationalId.trim() ||
      ownerFirstName.trim() ||
      ownerLastName.trim() ||
      economicCode.trim() ||
      legalPhone.trim() ||
      legalAddress.trim();
    const legalIdentity = hasIdentity
      ? {
          invoiceDisplayName: trimmedOrNull(invoiceDisplayName),
          legalName: trimmedOrNull(legalName),
          nationalId: trimmedOrNull(nationalId),
          ownerFirstName: trimmedOrNull(ownerFirstName),
          ownerLastName: trimmedOrNull(ownerLastName),
          economicCode: trimmedOrNull(economicCode),
          phone: trimmedOrNull(legalPhone),
          address: trimmedOrNull(legalAddress),
        }
      : null;
    return {
      personType: personType || null,
      industry: industry || null,
      industryOtherNote: trimmedOrNull(industryOtherNote),
      activityModel,
      teamSizeRange: teamSizeRange || null,
      fiscalYear,
      vat: vat || null,
      amountDisplayUnit: amountDisplayUnit || null,
      documentNumbering,
      primaryLocation,
      legalIdentity,
    };
  }

  function draftComplete(patch: BaseOrganizationPatch): boolean {
    return (
      patch.personType !== null &&
      patch.personType !== undefined &&
      patch.industry !== null &&
      patch.industry !== undefined &&
      patch.amountDisplayUnit !== null &&
      patch.amountDisplayUnit !== undefined
    );
  }

  async function handleSave() {
    setLocalError(null);
    await onSave(draft());
  }

  async function handleComplete() {
    setLocalError(null);
    const patch = draft();
    if (!draftComplete(patch)) {
      setLocalError('نوع شخصیت، حوزهٔ فعالیت و نمایش مبلغ باید مشخص شود.');
      return;
    }
    await onComplete(patch);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card>
        <V2CardHeader
          title="تنظیمات ثبت"
          subtitle="این مقادیر برای همهٔ سازمان‌ها یکسان و غیرقابل‌تغییرند."
        />
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>تقویم</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>شمسی</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>منطقهٔ زمانی</span>
            <span className="ltr" style={{ fontSize: 13, fontWeight: 600 }}>
              {invariants.timezone}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>واحد پول</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>ریال ایران</span>
          </div>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="سال مالی" />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!fyEditing ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 'var(--sk-r-lg)',
                border: '1px solid var(--sk-border)',
                background: 'var(--sk-bg-canvas)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{fyName}</span>
                <span
                  className="ltr sk-num"
                  style={{ fontSize: 11.5, color: 'var(--sk-fg-muted)' }}
                >
                  {fyStart} — {fyEnd}
                </span>
              </div>
              <V2Button variant="secondary" size="sm" onClick={() => setFyEditing(true)}>
                ویرایش
              </V2Button>
            </div>
          ) : (
            <>
              <V2FormGrid>
                <V2FormField label="کد" required width="short">
                  <input
                    className="sk-input ltr"
                    value={fyCode}
                    onChange={(e) => setFyCode(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="نام" required width="wide">
                  <input
                    className="sk-input"
                    value={fyName}
                    onChange={(e) => setFyName(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="شروع" required width="medium">
                  <JalaliDateInput value={fyStart} onChange={setFyStart} />
                </V2FormField>
                <V2FormField label="پایان" required width="medium">
                  <JalaliDateInput value={fyEnd} onChange={setFyEnd} />
                </V2FormField>
              </V2FormGrid>
              <V2Button
                variant="ghost"
                size="sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setFyEditing(false)}
              >
                تأیید
              </V2Button>
            </>
          )}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="ارزش افزوده" />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {VAT_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.value}
              value={opt.value}
              active={vat === opt.value}
              label={opt.label}
              description={opt.description || undefined}
              onClick={() => setVat(opt.value)}
            />
          ))}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader
          title="نمایش مبلغ *"
          subtitle="ذخیرهٔ مبلغ همچنان ریالی می‌ماند — این فقط شیوهٔ نمایش است."
        />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {AMOUNT_DISPLAY_OPTIONS.map((opt) => (
            <RadioRow
              key={opt.value}
              value={opt.value}
              active={amountDisplayUnit === opt.value}
              label={opt.label}
              description={opt.example}
              onClick={() => setAmountDisplayUnit(opt.value)}
            />
          ))}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="شماره‌گذاری اسناد" />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!docEditing ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '12px 14px',
                borderRadius: 'var(--sk-r-lg)',
                border: '1px solid var(--sk-border)',
                background: 'var(--sk-bg-canvas)',
              }}
            >
              <span className="ltr sk-num" style={{ fontSize: 13, fontWeight: 600 }}>
                نمونه: {docPrefix}-{samplePadded(docPadding)}
              </span>
              <V2Button variant="secondary" size="sm" onClick={() => setDocEditing(true)}>
                ویرایش
              </V2Button>
            </div>
          ) : (
            <>
              <V2FormGrid>
                <V2FormField label="پیشوند" required width="short">
                  <input
                    className="sk-input ltr"
                    value={docPrefix}
                    onChange={(e) => setDocPrefix(e.target.value)}
                    placeholder="INV"
                  />
                </V2FormField>
                <V2FormField label="سری" required width="short">
                  <input
                    className="sk-input ltr"
                    value={docSeries}
                    onChange={(e) => setDocSeries(e.target.value)}
                    placeholder="A"
                  />
                </V2FormField>
                <V2FormField
                  label="طول شماره"
                  required
                  width="medium"
                  hint={`نمونه: ${docPrefix || 'INV'}-${samplePadded(docPadding)}`}
                >
                  <select
                    className="sk-select"
                    value={docPadding}
                    onChange={(e) => setDocPadding(Number(e.target.value))}
                  >
                    {PADDING_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {p.toLocaleString('fa-IR')} رقم
                      </option>
                    ))}
                  </select>
                </V2FormField>
              </V2FormGrid>
              <V2Button
                variant="ghost"
                size="sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setDocEditing(false)}
              >
                تأیید
              </V2Button>
            </>
          )}
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="محل اصلی" subtitle="انبار یا شعبهٔ اصلی سازمان" />
        <div style={{ padding: '0 20px 20px' }}>
          <V2FormGrid>
            <V2FormField label="نام" width="wide">
              <input
                className="sk-input"
                value={locName}
                onChange={(e) => setLocName(e.target.value)}
                placeholder="دفتر مرکزی"
              />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader title="شناخت کسب‌وکار" />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              نوع شخصیت<span style={{ color: 'var(--sk-danger)' }}> *</span>
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PERSON_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  style={chipStyle(personType === opt.value)}
                  onClick={() => setPersonType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              حوزهٔ فعالیت (فقط یادداشت می‌شود)
              <span style={{ color: 'var(--sk-danger)' }}> *</span>
            </span>
            <SearchableSelect
              options={INDUSTRY_OPTIONS}
              selected={industry ? [industry] : []}
              placeholder="جست‌وجو…"
              onToggle={(v) => setIndustry(industry === v ? null : (v as SetupIndustry))}
            />
            {industry === 'other' ? (
              <input
                className="sk-input"
                placeholder="توضیح دهید…"
                value={industryOtherNote}
                onChange={(e) => setIndustryOtherNote(e.target.value)}
              />
            ) : null}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>مدل فعالیت</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ACTIVITY_MODEL_OPTIONS.map((opt) => {
                const active = activityModel.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    style={chipStyle(active)}
                    onClick={() =>
                      setActivityModel(
                        active
                          ? activityModel.filter((v) => v !== opt.value)
                          : [...activityModel, opt.value],
                      )
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <V2FormField label="تعداد تقریبی اعضای تیم (فقط یادداشت می‌شود)" width="medium">
            <select
              className="sk-select"
              value={teamSizeRange}
              onChange={(e) => setTeamSizeRange(e.target.value as SetupTeamSizeRange)}
            >
              <option value="">— انتخاب کنید —</option>
              {TEAM_SIZE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </V2FormField>
        </div>
      </V2Card>

      <V2Card>
        <V2CardHeader
          title="اطلاعاتی که روی اسناد شما نمایش داده می‌شود"
          subtitle="اختیاری — همیشه قابل رد کردن و ویرایش بعدی است."
        />
        <div style={{ padding: '0 20px 20px' }}>
          <V2FormGrid>
            <V2FormField label="نام روی پیش‌فاکتور و فاکتور" width="wide">
              <input
                className="sk-input"
                value={invoiceDisplayName}
                onChange={(e) => setInvoiceDisplayName(e.target.value)}
              />
            </V2FormField>
            {personType === 'company' ? (
              <>
                <V2FormField label="نام ثبتی شرکت" width="wide">
                  <input
                    className="sk-input"
                    value={legalName}
                    onChange={(e) => setLegalName(e.target.value)}
                    placeholder="شرکت کیمیا تجارت"
                  />
                </V2FormField>
                <V2FormField label="شناسهٔ ملی شرکت" width="medium">
                  <input
                    className="sk-input ltr"
                    value={nationalId}
                    onChange={(e) => setNationalId(e.target.value)}
                  />
                </V2FormField>
              </>
            ) : null}
            {personType === 'individual' ? (
              <>
                <V2FormField label="نام صاحب کسب‌وکار" width="medium">
                  <input
                    className="sk-input"
                    value={ownerFirstName}
                    onChange={(e) => setOwnerFirstName(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="نام خانوادگی صاحب کسب‌وکار" width="medium">
                  <input
                    className="sk-input"
                    value={ownerLastName}
                    onChange={(e) => setOwnerLastName(e.target.value)}
                  />
                </V2FormField>
              </>
            ) : null}
            <V2FormField label="شناسه/اطلاعات مالیاتی" width="medium">
              <input
                className="sk-input ltr"
                value={economicCode}
                onChange={(e) => setEconomicCode(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="تلفن" width="medium">
              <input
                className="sk-input ltr"
                value={legalPhone}
                onChange={(e) => setLegalPhone(e.target.value)}
              />
            </V2FormField>
            <V2FormField label="نشانی" width="wide">
              <input
                className="sk-input"
                value={legalAddress}
                onChange={(e) => setLegalAddress(e.target.value)}
              />
            </V2FormField>
          </V2FormGrid>
        </div>
      </V2Card>

      {localError || error ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '13px 16px',
            borderRadius: 'var(--sk-r-md)',
            background: 'var(--sk-danger-soft)',
            border: '1px solid color-mix(in oklch, var(--sk-danger), transparent 78%)',
          }}
        >
          <span style={{ color: 'var(--sk-danger)', display: 'flex' }}>
            <Icon name="x" size="sm" />
          </span>
          <span style={{ fontSize: 13, color: 'var(--sk-fg)' }}>{localError ?? error}</span>
        </div>
      ) : null}

      <V2FormActions>
        <V2Button variant="ghost" disabled={busy} onClick={onBack}>
          بازگشت
        </V2Button>
        <span style={{ marginInlineEnd: 'auto' }} />
        <V2Button variant="secondary" disabled={busy} onClick={() => void handleSave()}>
          {busy ? 'در حال ذخیره…' : 'ذخیرهٔ پیش‌نویس'}
        </V2Button>
        <V2Button
          variant="primary"
          icon="check"
          disabled={busy}
          onClick={() => void handleComplete()}
        >
          {busy ? 'در حال ثبت…' : 'تکمیل و ادامه'}
        </V2Button>
      </V2FormActions>
    </div>
  );
}
